import { type MouseEvent, type ReactNode, useEffect, useState } from 'react';

import type { ApiUserEnvironmentOrgMini } from '#/api/userEnvironment/types/ApiUserEnvironmentOrgMini';
import { ApiAndIntegrationsDropdown } from '#/components/ApiAndIntegrationsDropdown/ApiAndIntegrationsDropdown';
import { DropdownChevron } from '#/components/Dropdown/Chevron/DropdownChevron';
import { FindAJobDropdown } from '#/components/FindAJobDropdown/FindAJobDropdown';
import { IdealistLogo } from '#/components/Logo/IdealistLogo';
import { PageHeaderButton } from '#/components/PageHeader/Button/PageHeaderButton';
import { ParticipateDropdown } from '#/components/ParticipateDropdown/ParticipateDropdown';
import { PostOnIdealistDropdown } from '#/components/PostOnIdealistDropdown/PostOnIdealistDropdown';
import { VolunteerDropdown } from '#/components/VolunteerDropdown/VolunteerDropdown';
import { useIsClient } from '#/hooks/useIsClient';
import { useViewportBreakpoint } from '#/hooks/viewport/useViewportBreakpoint';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import { searchGetVolopsRoutePath } from '#/modules/search/volops/routing/searchGetVolopsRoutePath';
import { homeRoute } from '#/routing/routes';
import { trackClicked } from '#/utils/analytics/track/trackClicked';

import {
  PageHeaderApiAndIntegrationsDropdownHolder,
  PageHeaderAuthHolder,
  PageHeaderAuthLink,
  PageHeaderBottomSectionHolder,
  PageHeaderCartDropdownButtonHolder,
  PageHeaderDesktopDonateLinkHolder,
  PageHeaderDesktopSearchHolder,
  PageHeaderHolder,
  PageHeaderInnerHolder,
  PageHeaderLeft,
  PageHeaderLinksHolder,
  PageHeaderLogoLink,
  PageHeaderMobileSearchHolder,
  PageHeaderMyAccountHolder,
  PageHeaderNotificationsDropdownHolder,
  PageHeaderRight,
  PageHeaderSignupLinkHolder,
  PageHeaderTopSectionHolder,
} from './PageHeader.styled';

type Props = {
  userDisplayName: string | undefined;
  isUserLoaded: boolean;
  orgs: ApiUserEnvironmentOrgMini[];

  loginHref: string;
  onClickLogin: (event: MouseEvent<HTMLAnchorElement>) => void;

  signupHref: string;
  onClickSignup: (event: MouseEvent<HTMLAnchorElement>) => void;

  isInSearchPage: boolean;

  donate: {
    text: string;
    href: string;
  };

  myAccountDropdown: ReactNode;
  notificationsDropdown: ReactNode;
  cartDropdownButton: ReactNode;

  desktopSearchBar: ReactNode;
  mobileSearchDropdown: ReactNode;

  /*
   * HTML that needs to be sticky alongside the header
   */
  below: ReactNode;
};

const IDEALIST_DAYS_PATH = {
  en: '/en/days/get-involved',
  es: '/es/dias/participa',
  pt: '/pt/days/participe',
};

export function PageHeader({
  userDisplayName,
  isUserLoaded,
  orgs,
  loginHref,
  onClickLogin,
  signupHref,
  onClickSignup,
  isInSearchPage,
  donate,
  notificationsDropdown,
  myAccountDropdown,
  cartDropdownButton,
  desktopSearchBar,
  mobileSearchDropdown,
  below,
}: Props) {
  const { i18n, locale } = useI18n();
  const isLoggedIn = typeof userDisplayName !== 'undefined';

  const isClient = useIsClient();
  const viewport = useViewportBreakpoint();
  const isDesktop = isClient && ['md', 'lg'].includes(viewport);

  const [withScrolledTransition, setWithScrolledTransition] =
    useState(isInSearchPage);
  useEffect(() => {
    if (isInSearchPage === withScrolledTransition) return;
    setWithScrolledTransition(isInSearchPage);
  }, [isInSearchPage, withScrolledTransition]);

  return (
    <PageHeaderHolder data-qa-id="page-header" $isInSearchPage={isInSearchPage}>
      <PageHeaderInnerHolder>
        <PageHeaderTopSectionHolder>
          <PageHeaderLeft>
            <PageHeaderLogoLink
              to={homeRoute.with({ lang: locale })}
              title="Idealist"
              onClick={() => trackClicked('Idealist Logo Header')}
              qaId="header-logo-link"
            >
              <IdealistLogo withText height={{ mobile: 28, desktop: 36 }} />
            </PageHeaderLogoLink>

            <PageHeaderLinksHolder $isInSearchPage={isInSearchPage}>
              {locale === I18nLocale.ES || locale === I18nLocale.PT ? (
                <ParticipateDropdown
                  renderTrigger={({ triggerText, triggerHref }) => (
                    <PageHeaderButton
                      data={
                        isDesktop
                          ? {
                              type: 'link',
                              href: triggerHref,
                              onClick: () => {
                                trackClicked('Participate', {
                                  context: 'Header',
                                });
                              },
                            }
                          : { type: 'inert' }
                      }
                      variant="grey"
                      qaId="page-header-participate-dropdown-trigger"
                    >
                      {triggerText} <DropdownChevron />
                    </PageHeaderButton>
                  )}
                  trackClickedContext="Header Dropdown > Participate"
                />
              ) : (
                <PageHeaderButton
                  variant="grey"
                  href={IDEALIST_DAYS_PATH[locale]}
                  onClick={() =>
                    trackClicked('Idealist Days', { context: 'Header' })
                  }
                  type="link"
                >
                  Idealist Days
                </PageHeaderButton>
              )}

              <FindAJobDropdown
                renderTrigger={({ triggerText, triggerHref }) => (
                  <PageHeaderButton
                    data={
                      isDesktop
                        ? {
                            type: 'link',
                            href: triggerHref,
                            onClick: () => {
                              trackClicked('Find a Job', { context: 'Header' });
                            },
                          }
                        : { type: 'inert' }
                    }
                    variant="grey"
                    qaId="page-header-find-a-job-dropdown-trigger"
                  >
                    {triggerText} <DropdownChevron />
                  </PageHeaderButton>
                )}
                trackClickedContext="Header Dropdown > Find a Job"
              />

              {[I18nLocale.EN, I18nLocale.ES].includes(locale) ? (
                <VolunteerDropdown
                  renderTrigger={({ triggerText, triggerHref }) => (
                    <PageHeaderButton
                      variant="grey"
                      qaId="page-header-volunteer-dropdown-trigger"
                      data={
                        isDesktop
                          ? {
                              type: 'link',
                              href: triggerHref,
                              onClick: () => {
                                if (isDesktop) {
                                  trackClicked('Volunteer', {
                                    context: 'Header',
                                  });
                                }
                              },
                            }
                          : { type: 'inert' }
                      }
                    >
                      {triggerText} <DropdownChevron />
                    </PageHeaderButton>
                  )}
                  trackClickedContext="Header Dropdown > Volunteer"
                />
              ) : (
                <PageHeaderButton
                  type="link"
                  variant="grey"
                  qaId="nav-volop-link"
                  href={searchGetVolopsRoutePath({
                    locale,
                    embedSearchWidgetId: null,
                  })}
                  onClick={() =>
                    trackClicked('Volunteer', { context: 'Header' })
                  }
                >
                  {locale === I18nLocale.PT ? 'Voluntariar' : i18n('Volunteer')}
                </PageHeaderButton>
              )}

              <PostOnIdealistDropdown
                renderTrigger={({ triggerText, triggerHref }) => (
                  <PageHeaderButton
                    variant="grey"
                    qaId="page-header-post-on-idealist-dropdown-trigger"
                    data={
                      isDesktop
                        ? {
                            type: 'link',
                            href: triggerHref,
                            onClick: () => {
                              trackClicked('Post a Listing', {
                                context: 'Header',
                              });
                            },
                          }
                        : { type: 'inert' }
                    }
                  >
                    {triggerText} <DropdownChevron />
                  </PageHeaderButton>
                )}
                trackClickedContext="Header Dropdown > Post on Idealist"
              />

              {locale === I18nLocale.EN && (
                <PageHeaderApiAndIntegrationsDropdownHolder>
                  <ApiAndIntegrationsDropdown
                    renderTrigger={({ triggerText, triggerHref }) => (
                      <PageHeaderButton
                        data={
                          isDesktop
                            ? {
                                type: 'link',
                                href: triggerHref,
                                onClick: () => {
                                  trackClicked('API & Integrations', {
                                    context: 'Header',
                                  });
                                },
                              }
                            : { type: 'inert' }
                        }
                        variant="grey"
                        qaId="page-header-api-and-integrations-dropdown-trigger"
                      >
                        {triggerText} <DropdownChevron />
                      </PageHeaderButton>
                    )}
                    trackClickedContext="Header Dropdown > API & Integrations"
                  />
                </PageHeaderApiAndIntegrationsDropdownHolder>
              )}

              {orgs.length === 0 && (
                <PageHeaderDesktopDonateLinkHolder>
                  <PageHeaderButton
                    variant="grey"
                    type="link"
                    href={donate.href}
                    onClick={() =>
                      trackClicked('Donate', { context: 'Header' })
                    }
                  >
                    {donate.text}
                  </PageHeaderButton>
                </PageHeaderDesktopDonateLinkHolder>
              )}
            </PageHeaderLinksHolder>
          </PageHeaderLeft>

          <PageHeaderRight>
            {isUserLoaded && !isLoggedIn && (
              <PageHeaderAuthHolder>
                <PageHeaderAuthLink
                  href={loginHref}
                  rel="nofollow"
                  onClick={(e) => {
                    trackClicked('Log In Header');
                    onClickLogin(e);
                  }}
                  data-qa-id="login-link"
                  aria-label={i18n('Log In')}
                >
                  {i18n('Log In')}
                </PageHeaderAuthLink>

                <PageHeaderSignupLinkHolder>
                  <PageHeaderAuthLink
                    href={signupHref}
                    rel="nofollow"
                    onClick={(e) => {
                      trackClicked('Signup Header');
                      onClickSignup(e);
                    }}
                    data-qa-id="signup-link"
                    aria-label={i18n('Sign Up')}
                  >
                    {i18n('Sign Up')}
                  </PageHeaderAuthLink>
                </PageHeaderSignupLinkHolder>
              </PageHeaderAuthHolder>
            )}

            <PageHeaderMobileSearchHolder>
              {mobileSearchDropdown}
            </PageHeaderMobileSearchHolder>

            {isLoggedIn && (
              <PageHeaderMyAccountHolder>
                {myAccountDropdown}
              </PageHeaderMyAccountHolder>
            )}

            {isLoggedIn && (
              <PageHeaderNotificationsDropdownHolder>
                {notificationsDropdown}
              </PageHeaderNotificationsDropdownHolder>
            )}

            {orgs.length > 0 && (
              <PageHeaderCartDropdownButtonHolder>
                {cartDropdownButton}
              </PageHeaderCartDropdownButtonHolder>
            )}
          </PageHeaderRight>
        </PageHeaderTopSectionHolder>

        <PageHeaderBottomSectionHolder
          $isInSearchPage={isInSearchPage}
          $withScrolledTransition={isInSearchPage && withScrolledTransition}
        >
          {desktopSearchBar && (
            <PageHeaderDesktopSearchHolder data-qa-id="page-header-search-menu">
              {desktopSearchBar}
            </PageHeaderDesktopSearchHolder>
          )}

          {below}
        </PageHeaderBottomSectionHolder>
      </PageHeaderInnerHolder>
    </PageHeaderHolder>
  );
}
